import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import React, { useEffect, useState } from 'react';
import { PCRModel } from 'services/EpdClient';
import PcrService from 'services/PcrService';
import { useUpdateEpdPCRInformation } from 'services/api/mutations';
import { FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';
import { FieldPanelInput } from 'styles/v2/Styles.styled';
import { PCRInformationModel } from 'types/types';
import PcrSelector from '../../../pcr-selector/pcr-selector/PcrSelector';
import CPcrSelector from '../../../pcr-selector/c-pcr-selector/CPcrSelector';
import { usePcrSelectorDialogDeprecatedPcrDisclaimerText } from './usePcrSelectorDialogDeprecatedPcrDisclaimerText';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import usePcrV2 from '../../hooks/usePcrV2';

const PcrInformationPanel: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  pcrInformation: PCRInformationModel;
  validationState: any;
}> = ({ epdId, epdVersionId, pcrInformation, validationState }) => {
  const updateMutation = useUpdateEpdPCRInformation(epdVersionId);
  const isPCRV2Selected = usePcrV2();

  const pcrDisclaimerText = usePcrSelectorDialogDeprecatedPcrDisclaimerText();

  const onChange = (propertyName: string, newValue: any) => {
    updateMutation.mutate({
      requestData: { epdId, versionId: epdVersionId, propertyName, newValue },
    });
  };

  const onChangePcr = (propertyName: 'PCR' | 'CPCR', newPCR: PCRModel | undefined) => {
    updateMutation.mutate({
      requestData: { epdId, versionId: epdVersionId, propertyName, newValue: newPCR ? newPCR.id : newPCR },
      newOptimisticValue: newPCR,
    });
  };

  const [cpcrList, setCPcrList] = useState<PCRModel[]>([]);

  const getCPcrList = async () => {
    if (!pcrInformation?.pcr?.productCategories?.length) return;
    const selectedCategory = pcrInformation.pcr.productCategories[0];
    const res = await PcrService.findPcr({
      smartFilter: 'c-pcr',
      productCategoryId: selectedCategory.id,
      isDisabled: false,
      targetEPDFormat: 'compiler',
    });
    setCPcrList(res);
  };

  const removePcrOrCpcr = (type: 'PCR' | 'CPCR') => {
    if (type === 'PCR') {
      onChangePcr('PCR', undefined);
    }
    onChangePcr('CPCR', undefined);
  };

  const onDeletePcr = () =>
    confirmDelete({
      caption: `Are you sure you want to delete the ${pcrInformation.pcr?.fullName}?`,
      acceptLabel: 'Delete',
      description: !isPCRV2Selected ? pcrDisclaimerText : null,
      rejectLabel: 'Cancel',
      onConfirm: () => removePcrOrCpcr('PCR'),
    });

  const onDeleteCpcr = () =>
    confirmDelete({
      caption: `Are you sure you want to delete the ${pcrInformation.cpcr?.fullName}?`,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () => removePcrOrCpcr('CPCR'),
    });

  useEffect(() => {
    if (!pcrInformation?.pcr) {
      setCPcrList([]);
      return;
    }
    getCPcrList();
  }, [pcrInformation?.pcr]);

  const handleChangePcr = (pcr: PCRModel | null) => {
    if (pcr == null) {
      onDeletePcr();
    } else {
      onChangePcr('PCR', pcr);
      onChangePcr('CPCR', undefined);
    }
  };

  const handleChangeCPcr = (cpcr: PCRModel | null) => {
    if (cpcr == null) {
      onDeleteCpcr();
    } else {
      onChangePcr('CPCR', cpcr);
    }
  };

  const selectedCpcrCategory = pcrInformation.cpcrProductCategory
    ? {
        value: pcrInformation.cpcrProductCategory.id,
        label: pcrInformation.cpcrProductCategory.title,
      }
    : null;

  const cpcrCategoryOptions = pcrInformation.cpcr?.cpcrProductCategories?.map((x) => {
    return {
      value: x.id,
      label: x.title,
    };
  });

  const showCpcrSelector = !!pcrInformation.cpcr || !!cpcrList.length;

  const { errors, fieldsState } = validationState;

  return (
    <FieldPanelFullWidth>
      <FieldPanelHeader>PCR Information</FieldPanelHeader>
      <FieldPanelInput>
        <PcrSelector
          label="PCR"
          tooltip="Product category rules"
          tooltipPosition="right"
          pcr={pcrInformation.pcr ?? null}
          onChangePcr={handleChangePcr}
          error={errors.pcr}
          disabled={false}
          disclaimerText={pcrDisclaimerText}
          required
          onlyConstructionPcrs
        />
      </FieldPanelInput>
      {showCpcrSelector && (
        <FieldPanelInput>
          <CPcrSelector
            label="C-PCR"
            tooltip="Complementary product category rules"
            disclaimerText="Please make your selection from the available c-PCRs below. In order to create an EPD based on other c-PCRs that are not supported by EPD Compiler, please use the Standard EPD format."
            tooltipPosition="right"
            cpcr={pcrInformation.cpcr ?? null}
            cpcrList={cpcrList}
            onChangeCPcr={handleChangeCPcr}
            required={false}
            disabled={false}
          />
        </FieldPanelInput>
      )}
      {!!pcrInformation.cpcr && (
        <CompilerSelectControlledComponent
          label="Choose product category"
          tooltip="If possible, choose a fitting product category for your product(s)."
          name="cpcrProductCategory"
          options={cpcrCategoryOptions}
          required
          value={selectedCpcrCategory}
          onChanged={onChange}
          placeholder="Select"
          state={fieldsState}
          error={errors.cpcrProductCategory}
          isClearable
        />
      )}
    </FieldPanelFullWidth>
  );
};

export default PcrInformationPanel;
